import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { gameDayToShortDate, gameDayToFullDate } from '../utils/dateUtils';
import './GamesPage.css';

interface GameFromDB {
  id: string;
  league_id: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  game_day: number;
  status: string;
  winner: string;
}

interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
}

const GamesPage: React.FC = () => {
  const [view, setView] = useState<'upcoming' | 'completed'>('completed');
  const [games, setGames] = useState<GameFromDB[]>([]);
  const [teams, setTeams] = useState<Map<string, TeamFromDB>>(new Map());
  const [loading, setLoading] = useState(true);
  const [simState, setSimState] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (simState?.is_running) {
      const interval = setInterval(loadData, 10000);
      return () => clearInterval(interval);
    }
  }, [simState?.is_running]);

  async function loadData() {
    setLoading(true);

    if (isSupabaseConfigured() && supabase) {
      // Load all teams for name lookups
      const { data: teamData } = await supabase
        .from('teams')
        .select('id, name, city, abbreviation');

      if (teamData) {
        const teamMap = new Map<string, TeamFromDB>();
        (teamData as TeamFromDB[]).forEach(t => teamMap.set(t.id, t));
        setTeams(teamMap);
      }

      // Load simulation state from leagues table (not simulation_state, which is global/legacy)
      // In multi-league, each league has its own current_day and season_year
      const { data: leaguesData } = await supabase
        .from('leagues')
        .select('id, current_day, season_year, status')
        .order('tier')
        .limit(1);
      
      if (leaguesData && leaguesData.length > 0) {
        setSimState({
          current_day: leaguesData[0].current_day,
          season_year: leaguesData[0].season_year,
          status: leaguesData[0].status,
        } as any);
      }

      // Load games based on view
      const status = view === 'completed' ? 'completed' : 'scheduled';
      const { data: gameData } = await supabase
        .from('league_schedule')
        .select('*')
        .eq('status', status)
        .order('game_day', { ascending: view === 'completed' ? false : true })
        .limit(100);

      if (gameData) setGames(gameData as GameFromDB[]);
    }

    setLoading(false);
  }

  // Reload when view changes
  useEffect(() => {
    loadData();
  }, [view]);

  const getTeamDisplay = (teamId: string): string => {
    const team = teams.get(teamId);
    return team ? (team.name.startsWith(team.city) ? team.name : `${team.city} ${team.name}`) : 'Unknown';
  };

  // Group games by day
  const gamesByDay = games.reduce((acc, game) => {
    const day = game.game_day;
    if (!acc.has(day)) acc.set(day, []);
    acc.get(day)!.push(game);
    return acc;
  }, new Map<number, GameFromDB[]>());

  if (loading) {
    return (
      <div className="games-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading games...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="games-page">
      <div className="page-header">
        <h1>⚾ Games</h1>
        {simState && (
          <p className="subtitle">
            {simState?.current_day > 0
              ? (simState?.is_running ? `🔴 Simulating — ${gameDayToShortDate(simState.current_day)}` : gameDayToFullDate(simState.current_day))
              : 'Season not started'}
          </p>
        )}
      </div>

      <div className="view-tabs">
        <button
          className={`view-tab ${view === 'completed' ? 'active' : ''}`}
          onClick={() => setView('completed')}
        >
          ✅ Completed
        </button>
        <button
          className={`view-tab ${view === 'upcoming' ? 'active' : ''}`}
          onClick={() => setView('upcoming')}
        >
          📅 Upcoming
        </button>
      </div>

      {games.length === 0 ? (
        <div className="empty-state">
          <p>{view === 'completed' ? 'No completed games yet.' : 'No upcoming games scheduled.'}</p>
          <p className="hint">
            {view === 'completed'
              ? 'Games will appear here after simulation runs.'
              : 'Advance to Day 1 in the Commissioner panel to generate the schedule.'}
          </p>
        </div>
      ) : (
        <div className="games-feed">
          {Array.from(gamesByDay.entries()).map(([day, dayGames]) => (
            <div key={day} className="day-group">
              <h3 className="day-header">{gameDayToFullDate(day)}</h3>
              <div className="day-games">
                {dayGames.map(game => {
                  const homeWon = game.home_score > game.away_score;
                  return (
                    <div key={game.id} className="game-card">
                      <div className="game-matchup">
                        <div className={`team-line ${!homeWon && view === 'completed' ? 'winner' : ''}`}>
                          <span className="team-label" style={{cursor:'pointer',color:'#60a5fa'}} onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'team-' + game.away_team_id }))}>{getTeamDisplay(game.away_team_id)}</span>
                          {view === 'completed' && (
                            <span className="score">{game.away_score}</span>
                          )}
                        </div>
                        <div className="at-divider">@</div>
                        <div className={`team-line ${homeWon && view === 'completed' ? 'winner' : ''}`}>
                          <span className="team-label" style={{cursor:'pointer',color:'#60a5fa'}} onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'team-' + game.home_team_id }))}>{getTeamDisplay(game.home_team_id)}</span>
                          {view === 'completed' && (
                            <span className="score">{game.home_score}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GamesPage;
